<template>
  <v-container>
    <v-breadcrumbs :items="breadcrumbs" divider=">"></v-breadcrumbs>
    <h1 class="headline pb-4 mx-lg-auto">Staff Members</h1>
    <v-toolbar flat color="toolbar-action transparent">
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
        z-index="20"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            autocomplete="off"
            v-model="search.keyword"
            label=""
            placeholder="Search Staff Members"
            prepend-inner-icon="mdi-magnify"
            filled
            rounded
            dense
            clearable
            v-bind="attrs"
            v-on="on"
            class="v-input--search"
            @click:clear="clearSearchAction"
            @keydown.enter="searchAction()"
          ></v-text-field>
        </template>
        <StaffMemberSearchForm
          @clicked-search-button="clickedSearchButtonInModal"
          @clicked-clear-button="clearSearchAction"
          :meta="meta"
          :initialValues="getInitialFormValues()"
        />
      </v-menu>
      <v-spacer></v-spacer>
      <v-btn color="primary v-btn--new" dark class="ma-2" to="/staff-members/new">New</v-btn>
    </v-toolbar>
    <v-card>
      <v-card-text>
        <Paginate :meta="meta" v-on:paginate="paginateAction" />
        <v-data-table
          :headers="headers"
          :items="staffMembers"
          :options.sync="options"
          @click:row="editItem"
          :loading="loading"
          loading-text="Loading... Please wait"
          hide-default-footer
        >
          <template v-slot:item.actions="{ item }">
            <v-spacer></v-spacer>
            <v-icon small @click.stop="deleteItem(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <ConfirmDialog
      title="Confirm delete"
      description="Are you sure you want to delete this item"
      okayLabel="Yes"
      cancelLabel="No"
      :show="showDeleteConfirmDialog"
      @confirm-dialog-clicked="confirmDelete"
    />
  </v-container>
</template>
<style scoped>
.v-input--search {
  max-width: 80%;
}
.v-btn--new {
  position: absolute;
  top: -12px;
  right: 0;
}
.v-menu__content {
  max-width: 640px;
  padding: 20px 20px 0 20px;
  background: white;
}
</style>
<script>
import { mapGetters } from 'vuex';
import store from '@/store/index';
import ConfirmDialog from '@/views/dialogs/ConfirmDialog';
import StaffMemberSearchForm from '@/views/StaffMemberSearchForm';
import Paginate from '@/views/Paginate';

export default {
  name: 'StaffMembersView',
  components: {
    StaffMemberSearchForm,
    Paginate,
    ConfirmDialog
  },
  beforeRouteEnter(to, from, next) {
    const currentPage = parseInt(to.query.page) || 1;
    const sortBy = to.query.sort_by || 'surname';
    const sortDesc = to.query.sort_desc === undefined ? false : to.query.sort_desc === 'true';
    const keyword = to.query.keyword || '';
    const active = to.query.active === 'true';

    if (keyword || active) {
      let searchParams = {
        page: currentPage,
        sort_by: sortBy,
        sort_desc: sortDesc
      };

      if (keyword) {
        searchParams.keyword = keyword;
      } else {
        // Add additional search parameters if they exist in the query
        if (to.query.first_name) {
          searchParams.first_name = to.query.first_name;
        }
        if (to.query.surname) {
          searchParams.surname = to.query.surname;
        }
        if (to.query.staff_statuses_id) {
          searchParams.staff_statuses_id = to.query.staff_statuses_id;
        }
        if (to.query.staff_types_id) {
          searchParams.staff_types_id = to.query.staff_types_id;
        }
        if (to.query.suburb) {
          searchParams.suburb = to.query.suburb;
        }
        if (to.query.card_expire_from) {
          searchParams.card_expire_from = to.query.card_expire_from;
        }
        if (to.query.card_expire_to) {
          searchParams.card_expire_to = to.query.card_expire_to;
        }
      }

      store.dispatch('staffMember/search', searchParams).then(() => {
        to.params.page = currentPage;
        next(vm => {
          if (keyword) {
            vm.search.keyword = keyword;
            vm.search.active = true;
          }
        });
      });
    } else {
      // Browse default staff members
      store
        .dispatch('staffMember/list', {
          page: currentPage,
          sort_by: sortBy,
          sort_desc: sortDesc
        })
        .then(() => {
          to.params.page = currentPage;
          next();
        });
    }
  },
  methods: {
    load(updateQuery = true) {
      const options = {
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'surname',
        sort_desc: this.options.sortDesc[0] ?? false
      };

      if (updateQuery) {
        this.$router.push({
          query: options
        });
      }

      store.dispatch('staffMember/list', options);
    },
    editItem(item) {
      this.$router.push({ path: `/staff-members/${item.id}` });
    },
    deleteItem(item) {
      this.staff_member_id = item.id;
      this.showDeleteConfirmDialog = true;
    },
    confirmDelete: function (deleteItem) {
      this.showDeleteConfirmDialog = false;
      if (deleteItem) {
        this.processDelete();
      }
    },
    async processDelete() {
      await store.dispatch('staffMember/delete', this.staff_member_id);
      this.options.page = parseInt(this.$route.query.page) || 1;
      this.load(false);
    },
    paginateAction(item, page) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: page
        }
      });

      if (item == 'next') {
        store.dispatch('staffMember/paginate', this.links.next);
      } else if (item == 'previous') {
        store.dispatch('staffMember/paginate', this.links.prev);
      }
    },
    clickedSearchButtonInModal(options) {
      this.menu = false;
      const defaultOptions = {
        active: true,
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'surname',
        sort_desc: this.options.sortDesc[0] ?? false
      };
      this.searchQuery({
        ...defaultOptions,
        ...options
      });
    },
    searchAction() {
      this.searchQuery({
        keyword: this.search.keyword,
        active: true,
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'surname',
        sort_desc: this.options.sortDesc[0] ?? false
      });
    },
    searchQuery(options) {
      Object.keys(options).forEach(key => {
        if (options[key] === null || options[key] === undefined) {
          delete options[key];
        }
      });

      store.dispatch('staffMember/search', options);
      this.$router.push({
        query: options
      });

      this.search = options;
      this.menu = false;
    },
    clearSearchAction() {
      this.search = {
        keyword: '',
        active: false
      };

      this.$router.push({
        query: {}
      });

      this.load(false);
    },
    getInitialFormValues() {
      const query = this.$route.query;
      return {
        first_name: query.first_name || null,
        surname: query.surname || null,
        suburb: query.suburb || null,
        staff_types_id: Number(query.staff_types_id) || null,
        staff_statuses_id: Number(query.staff_statuses_id) || null,
        card_expire_from: query.card_expire_from || null,
        card_expire_to: query.card_expire_to || null
      };
    }
  },
  computed: {
    ...mapGetters('staffMember', ['loading', 'error', 'staffMembers', 'meta', 'links'])
  },
  watch: {
    options: {
      handler() {
        if (this.options.init) {
          if (this.search.active) {
            this.search.page = this.options.page;
            this.search.sort_by = this.options.sortBy[0] ?? 'surname';
            this.search.sort_desc = this.options.sortDesc[0] ?? false;
            this.searchQuery(this.search);
          } else {
            this.load();
          }
        } else {
          this.options.init = true;
        }
      },
      deep: true
    }
  },
  data: () => ({
    menu: false,
    search: {
      keyword: '',
      active: false
    },
    showDeleteConfirmDialog: false,
    options: {},
    headers: [
      { text: 'Firstname', value: 'first_name' },
      { text: 'Surname', value: 'surname' },
      { text: 'Email', value: 'email' },
      { text: 'Mobile', value: 'mobile' },
      { text: 'Status', value: 'status' },
      { text: 'Type', value: 'type' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
    ],
    breadcrumbs: [
      {
        text: 'Dashboard',
        exact: true,
        to: '/dashboard'
      },
      {
        text: 'Staff Members',
        disabled: true,
        exact: true,
        to: '/staff-members'
      }
    ]
  })
};
</script>
